import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Frame, ROUND, type Photos, type Trim } from '@/components/print/Builder'

// The card builder's screen: the set of cards in a pile beside the panel of choices. The card on top flips over when
// tapped to show its back, and the pile deals forward and back with the arrows or a swipe. `thisCard` gives the
// choices for just the card on top, under the pile.

export type Card = { name: string; front: () => ReactNode; back: () => ReactNode }

// How many cards show in the pile under the top one.
const UNDER = 3

export function CardBuilder({
  label,
  badge,
  back,
  trim,
  cards,
  thisCard,
  photos,
  children,
}: {
  label: string
  badge: string
  back: { to: string; label: string }
  trim: Trim
  cards: Card[]
  thisCard?: (at: number) => ReactNode
  photos?: Photos
  children: ReactNode
}) {
  const [open, setOpen] = useState(0)
  const [flipped, setFlipped] = useState(false)
  const at = Math.min(open, Math.max(0, cards.length - 1))
  const move = (to: number) => {
    if (to < 0 || to >= cards.length) return
    setOpen(to)
    setFlipped(false)
  }
  // A swipe across the pile deals the next card or brings back the last; a tap flips it.
  const drag = useRef<number | null>(null)
  const swiped = useRef(false)
  const card = cards[at]

  return (
    <Frame
      label={label}
      heading="Print recipe cards"
      badge={badge}
      back={back}
      photos={photos}
      stage={
        <section aria-label="The cards" className="flex min-w-0 flex-col gap-3 lg:sticky lg:top-20">
          <Stage trim={trim}>
            {cards.map((x, i) => {
              const d = i - at
              if (d < -1 || d > UNDER) return null
              const style: CSSProperties = {
                zIndex: cards.length - i,
                opacity: d < 0 || d === UNDER ? 0 : 1,
                transform:
                  d < 0
                    ? 'translateX(-115%) rotate(-6deg)'
                    : d === 0
                      ? flipped
                        ? 'rotateY(180deg)'
                        : 'none'
                      : `translate(${d * 0.6}rem, ${-d * 0.5}rem) rotate(${d * 2}deg)`,
              }
              const top = d === 0
              return (
                <div key={i} className="kp-card" style={style} aria-hidden={!top} inert={!top}>
                  <div className="kp-card-face">{x.front()}</div>
                  <div className="kp-card-face is-back">{x.back()}</div>
                </div>
              )
            })}
            {card && (
              <button
                type="button"
                aria-label={flipped ? 'Turn the card back over' : 'Flip the card'}
                className="absolute inset-0 z-[999] cursor-pointer rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60"
                style={{ touchAction: 'pan-y' }}
                onPointerDown={(e) => {
                  drag.current = e.clientX
                  swiped.current = false
                }}
                onPointerCancel={() => (drag.current = null)}
                onPointerUp={(e) => {
                  const dx = drag.current === null ? 0 : e.clientX - drag.current
                  drag.current = null
                  if (Math.abs(dx) < 40) return
                  swiped.current = true
                  move(at + (dx < 0 ? 1 : -1))
                }}
                onClick={() => {
                  // A swipe ends in a click too; only a tap (or a key) flips.
                  if (swiped.current) swiped.current = false
                  else setFlipped(!flipped)
                }}
              />
            )}
          </Stage>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              aria-label="The card before"
              disabled={at === 0}
              onClick={() => move(at - 1)}
              className={ROUND}
            >
              <ChevronLeft aria-hidden className="size-5" />
            </button>
            <p
              aria-live="polite"
              data-testid="card-where"
              className="min-w-40 text-center text-sm text-muted-foreground tabular-nums"
            >
              {card ? `Card ${at + 1} of ${cards.length}, ${flipped ? 'back' : 'front'}` : 'No cards yet'}
            </p>
            <button
              type="button"
              aria-label="The next card"
              disabled={at >= cards.length - 1}
              onClick={() => move(at + 1)}
              className={ROUND}
            >
              <ChevronRight aria-hidden className="size-5" />
            </button>
          </div>
          <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
            <RefreshCw aria-hidden className="size-3" /> Tap a card to flip it, or swipe to the next.
          </p>
          {thisCard && card && <div className="grid gap-2 sm:grid-cols-2">{thisCard(at)}</div>}
        </section>
      }
    >
      {children}
    </Frame>
  )
}

// The space the pile sits in: a card as large as fits, keeping the card's shape, with room around it for the pile.
function Stage({ trim, children }: { trim: Trim; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) =>
      setSize({ width: entry!.contentRect.width, height: entry!.contentRect.height }),
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const ratio = trim.width / trim.height
  const width = Math.min(size.width * 0.86, size.height * 0.8 * ratio)
  return (
    <div ref={ref} className="grid h-[min(46svh,30rem)] min-h-64 place-items-center lg:h-[calc(100svh-19rem)]">
      <div className="kp-deck relative" style={{ width, aspectRatio: `${trim.width} / ${trim.height}` }}>
        {width > 0 && children}
      </div>
    </div>
  )
}
